import { Stack, Group, Text, Box, Badge } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDate, formatMinor } from '../../../../lib/format';
import { chargeInForce } from '../../../../lib/priceLadder';
import { RateChangeButton, rateMarginText } from './RateVersionModal';
import { THIN_MARGIN_PERCENT, RateStatus, live } from './SkuRatesTable';
import { CancelUpcomingButton } from './CancelUpcomingButton';
import { PlanPrices, type PlanLabel } from './PlanPrices';
import { useT } from '../../../../i18n/I18nProvider';

type Sku = NonNullable<Awaited<ReturnType<typeof admin.listSkuRates>>['data']>[number];

const MUTED = 'var(--rr-color-text-muted)';

/**
 * The price book as rows: the item on the left with what each plan pays under
 * it, the four money figures on the right. The charge is the list price in
 * force and the margin is the service's own figure on it, as in the table.
 */
export function SkuRatesList({ records, showRetired, plans }: { records: Sku[]; showRetired: boolean; plans: PlanLabel[] }) {
  const t = useT();
  return (
    <Stack gap="xs">
      {records.map((s) => {
        const list = chargeInForce(s);
        const thin = live(s) && list > 0 && (s.margin_percent ?? 0) < THIN_MARGIN_PERCENT;
        return (
          <div key={s.id} className="rr-list-row" style={{ cursor: 'default' }}>
            <Group gap="md" align="center" wrap="nowrap" style={{ width: '100%' }}>
              <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
                <Group gap="xs" align="center" wrap="nowrap">
                  <Text role="subtitle" fw={600} truncate>{s.name}</Text>
                  <Badge size="xs" variant="light" color="gray">{t(`billing.skuRates.category.${s.category}`, s.category)}</Badge>
                  {s.follows_ladder && (
                    <Tooltip label={t('billing.skuRates.ladderBadgeHint')} position="top">
                      <Badge size="xs" variant="light" color="blue">{t('billing.skuRates.ladderBadge')}</Badge>
                    </Tooltip>
                  )}
                  {(showRetired || s.status === 'upcoming') && <RateStatus rate={s} />}
                </Group>
                <Text role="caption" c={MUTED} truncate>
                  <code>{s.sku_code}</code> · {t(`billing.skuRates.unit.${s.unit}`, s.unit)} · {t('billing.skuRates.list.effectiveFrom', { date: formatDate(s.effective_from) })}
                </Text>
                {s.plan_amounts_minor && <PlanPrices amounts={s.plan_amounts_minor} plans={plans} currency={s.currency} />}
              </Stack>
              <Box className="rr-skurates-figures">
                <Figure label={t('billing.skuRates.columns.cost')} value={formatMinor(s.cost_minor, s.currency)} muted />
                <Figure
                  label={t('billing.skuRates.columns.floor')}
                  value={s.floor_minor == null ? '–' : s.floor_minor > 0 ? formatMinor(s.floor_minor, s.currency) : t('billing.skuRates.noFloor')}
                  muted
                />
                <Figure label={t('billing.skuRates.columns.charge')} value={formatMinor(list, s.currency)} muted={!live(s)} />
                <Figure
                  label={t('billing.skuRates.columns.margin')}
                  value={rateMarginText(s)}
                  colour={thin ? 'var(--rr-color-error)' : undefined}
                />
              </Box>
              <Box style={{ flexShrink: 0, minWidth: 72, textAlign: 'right' }}>
                {s.status === 'current' ? <RateChangeButton rate={s} /> : s.status === 'upcoming' ? <CancelUpcomingButton rate={s} /> : null}
              </Box>
            </Group>
          </div>
        );
      })}
    </Stack>
  );
}

function Figure({ label, value, muted, colour }: { label: string; value: string; muted?: boolean; colour?: string }) {
  return (
    <span className="rr-skurates-figure">
      <Text role="overline" c="var(--rr-color-text-muted)">{label}</Text>
      <Text role="bodySmall" fw={600} className="rr-billing-money" c={colour ?? (muted ? 'var(--rr-color-text-muted)' : undefined)}>{value}</Text>
    </span>
  );
}
